import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { UploadCloud, Check } from "lucide-react";
import { postJSON } from "../hooks/useMetals";
import { useLang } from "../i18n";

const ROLES = [
  ["designer", { en: "Independent jewelry designer", it: "Designer di gioielli indipendente" }],
  ["brand", { en: "Jewelry or fashion brand", it: "Brand di gioielleria o moda" }],
  ["retailer", { en: "Retail jeweler / boutique", it: "Rivenditore / gioielleria" }],
  ["other", { en: "Other", it: "Altro" }],
];
const PROJECT_TYPES = [
  ["new", { en: "New collection — design + production", it: "Nuova collezione — design + produzione" }],
  ["proto", { en: "Prototype / sample only", it: "Solo prototipo / campione" }],
  ["prod", { en: "Production run", it: "Tiratura di produzione" }],
  ["custom", { en: "Single custom piece", it: "Pezzo unico su misura" }],
  ["pl", { en: "Private label manufacturing", it: "Produzione private label" }],
  ["help", { en: "Not sure — need guidance", it: "Non so ancora — ho bisogno di una guida" }],
];
const QUANTITIES = [
  ["1-5", { en: "1–5 pieces (prototype)", it: "1–5 pezzi (prototipo)" }],
  ["6-25", { en: "6–25 pieces (small run)", it: "6–25 pezzi (piccola tiratura)" }],
  ["26-100", { en: "26–100 pieces", it: "26–100 pezzi" }],
  ["100+", { en: "100+ pieces (production)", it: "100+ pezzi (produzione)" }],
  ["?", { en: "Not sure yet", it: "Non so ancora" }],
];
const MATERIALS = [
  ["ag925", { en: "Sterling silver 925", it: "Argento 925" }],
  ["au9", { en: "Gold 9k", it: "Oro 9k" }],
  ["au18", { en: "Gold 18k", it: "Oro 18k" }],
  ["au24", { en: "Gold 24k", it: "Oro 24k" }],
  ["brass", { en: "Brass or bronze", it: "Ottone o bronzo" }],
  ["help", { en: "Not sure — advise me", it: "Non so — consigliami" }],
];
const STONES = [
  ["none", { en: "No stones", it: "Nessuna pietra" }],
  ["own", { en: "Yes — I will supply the stones", it: "Sì — fornirò le pietre io" }],
  ["src", { en: "Yes — please help me source", it: "Sì — aiutami nel reperimento" }],
  ["?", { en: "Not sure yet", it: "Non so ancora" }],
];
const TIMELINES = [
  ["urgent", { en: "Within 4 weeks (urgent — please call us)", it: "Entro 4 settimane (urgente — chiamaci)" }],
  ["1-3m", { en: "1–3 months", it: "1–3 mesi" }],
  ["3-6m", { en: "3–6 months", it: "3–6 mesi" }],
  ["6m+", { en: "6+ months / no rush", it: "6+ mesi / senza fretta" }],
  ["?", { en: "Not sure yet", it: "Non so ancora" }],
];

const INCLUDE_ITEMS = [
  { en: <><strong>Type of piece</strong> — ring, pendant, bracelet, earrings</>, it: <><strong>Tipo di pezzo</strong> — anello, ciondolo, bracciale, orecchini</> },
  { en: <><strong>Your design</strong> — sketch, photo, or description (anything works)</>, it: <><strong>Il tuo design</strong> — bozzetto, foto o descrizione</> },
  { en: <><strong>Preferred metal</strong> — or tell us the end use and we'll advise</>, it: <><strong>Metallo preferito</strong> — o dicci l'utilizzo finale</> },
  { en: <><strong>Quantity</strong> — even a rough estimate helps</>, it: <><strong>Quantità</strong> — anche una stima approssimativa</> },
  { en: <><strong>Timeline</strong> — if you have one in mind</>, it: <><strong>Tempi</strong> — se ne hai uno in mente</> },
];

function SectionTitle({ children }) {
  return (
    <div className="mono-tech mb-5 flex items-center gap-3 text-[#8A8F82]">
      <span>{children}</span>
      <span className="h-px flex-1 bg-[#0E3B33]/10" />
    </div>
  );
}

function Field({ label, required, children, testid }) {
  return (
    <div data-testid={testid}>
      <label className="mono-tech mb-2 block text-[#4A5248]">
        {label} {required && <span className="text-[#A8853B]">*</span>}
      </label>
      {children}
    </div>
  );
}

export default function Contact() {
  const { L } = useLang();
  const location = useLocation();
  const prefill = location.state?.prefill || "";
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [fileNames, setFileNames] = useState([]); // uploaded storage records {id, name, size}
  const [uploading, setUploading] = useState(false);

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries/upload`, { method: "POST", body: fd });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(typeof d.detail === "string" ? d.detail : "upload failed");
        setFileNames((prev) => [...prev, { id: d.id, name: d.name, size: d.size }]);
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };
  const [form, setForm] = useState({
    name: "", company: "", email: "", country: "", role: "",
    project_type: "", quantity: "", material: "", stones: "none",
    message: prefill, timeline: "",
  });

  useEffect(() => {
    if (prefill) setForm((f) => ({ ...f, message: prefill }));
  }, [prefill]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await postJSON("/enquiries", { ...form, files: fileNames });
      setSent(true);
      toast.success(L({ en: "Brief received — we reply within 48 hours.", it: "Brief ricevuto — rispondiamo entro 48 ore." }));
      window.__lenis?.scrollTo(0);
    } catch {
      toast.error(L({ en: "Something went wrong sending your brief. Please try again.", it: "Errore nell'invio del brief. Riprova." }));
    } finally {
      setBusy(false);
    }
  };

  const selectCls = "forge-field appearance-none cursor-pointer";

  return (
    <div data-testid="contact-page" className="grid lg:grid-cols-2">
      {/* LEFT PANEL */}
      <aside className="flex flex-col justify-between bg-[#0E3B33] px-8 py-16 lg:sticky lg:top-[104px] lg:min-h-[calc(100vh-104px)] lg:px-14 lg:py-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}>
          <div className="mb-8 h-px w-14 bg-[#C9A84C]" />
          <h1 className="font-display text-4xl leading-tight tracking-tight text-[#FBF9F3] sm:text-5xl" data-testid="contact-headline">
            {L({ en: <>Tell us what<br />you're making.</>, it: <>Raccontaci cosa<br />stai creando.</> })}
          </h1>
          <p className="mt-6 max-w-md text-sm leading-relaxed text-[#FBF9F3]/70">
            {L({
              en: "We respond within 48 hours. All briefs are treated in strict confidence — we never share client project details with third parties.",
              it: "Rispondiamo entro 48 ore. Tutti i brief sono trattati con la massima riservatezza — non condividiamo mai i dettagli dei progetti dei clienti con terzi.",
            })}
          </p>
          <p className="mono-tech mt-10 mb-4 text-[#C9A84C]">{L({ en: "What to include in your brief", it: "Cosa includere nel brief" })}</p>
          <ul className="space-y-2.5">
            {INCLUDE_ITEMS.map((item, i) => (
              <li key={i} className="flex gap-3 text-sm text-[#FBF9F3]/65" data-testid={`include-item-${i}`}>
                <span className="shrink-0 text-[#C9A84C]">—</span>
                <span className="[&_strong]:font-medium [&_strong]:text-[#FBF9F3]">{L(item)}</span>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-xs italic text-[#FBF9F3]/40">
            {L({
              en: `You don't need perfect drawings — "not sure yet" is a valid answer for any field.`,
              it: `Non hai bisogno di disegni perfetti — "non so ancora" è una risposta valida.`,
            })}
          </p>
        </motion.div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="mt-14 hidden border-t border-white/10 pt-8 lg:block"
        >
          <blockquote className="font-display text-lg italic leading-relaxed text-[#FBF9F3]/65">
            {L({
              en: `"We work as partners, not just producers. We listen to what a piece needs to be — and return something that feels like it was always meant to exist."`,
              it: `"Lavoriamo come partner, non solo come produttori. Ascoltiamo cosa deve essere un pezzo — e restituiamo qualcosa che sembra sempre dover esistere."`,
            })}
          </blockquote>
          <cite className="mono-tech mt-4 block not-italic text-[#C9A84C]">DA Creations Atelier — Urbino, Marche, Italy</cite>
        </motion.div>
      </aside>

      {/* RIGHT PANEL — FORM */}
      <main className="px-8 py-16 lg:px-14 lg:py-20">
        {!sent ? (
          <motion.form
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
            onSubmit={submit}
            data-testid="contact-enquiry-form"
            className="mx-auto max-w-xl"
          >
            {prefill && (
              <div className="mb-8 rounded-md border border-[#C9A84C]/40 bg-[#C9A84C]/10 px-4 py-3 text-xs text-[#8A6D2B]" data-testid="prefill-notice">
                {L({ en: "Your live estimate from the workbench is attached to the message below.", it: "Il tuo preventivo live dal banco di lavoro è allegato al messaggio qui sotto." })}
              </div>
            )}

            <div className="mb-10 border-b border-[#0E3B33]/10 pb-10">
              <SectionTitle>{L({ en: "About you", it: "Chi sei" })}</SectionTitle>
              <div className="grid gap-5 sm:grid-cols-2">
                <Field label={L({ en: "Full name", it: "Nome completo" })} required testid="field-name">
                  <input required data-testid="contact-name-input" className="forge-field" placeholder="Jane Rossi" value={form.name} onChange={set("name")} />
                </Field>
                <Field label={L({ en: "Company / brand", it: "Azienda / brand" })} testid="field-company">
                  <input data-testid="contact-company-input" className="forge-field" placeholder={L({ en: "Optional", it: "Opzionale" })} value={form.company} onChange={set("company")} />
                </Field>
                <Field label="Email" required testid="field-email">
                  <input required type="email" data-testid="contact-email-input" className="forge-field" placeholder="jane@yourbrand.com" value={form.email} onChange={set("email")} />
                </Field>
                <Field label={L({ en: "Country", it: "Paese" })} testid="field-country">
                  <input data-testid="contact-country-input" className="forge-field" placeholder={L({ en: "United Kingdom", it: "Italia" })} value={form.country} onChange={set("country")} />
                </Field>
              </div>
              <div className="mt-5">
                <Field label={L({ en: "I am a…", it: "Sono…" })} required testid="field-role">
                  <select required data-testid="contact-role-select" className={selectCls} value={form.role} onChange={set("role")}>
                    <option value="">{L({ en: "Select the best description", it: "Seleziona la descrizione più adatta" })}</option>
                    {ROLES.map(([v, l]) => <option key={v} value={v}>{L(l)}</option>)}
                  </select>
                </Field>
              </div>
            </div>

            <div className="mb-10 border-b border-[#0E3B33]/10 pb-10">
              <SectionTitle>{L({ en: "Your project", it: "Il tuo progetto" })}</SectionTitle>
              <div className="space-y-5">
                <Field label={L({ en: "Type of project", it: "Tipo di progetto" })} required testid="field-project-type">
                  <select required data-testid="contact-project-type-select" className={selectCls} value={form.project_type} onChange={set("project_type")}>
                    <option value="">{L({ en: "What are you looking to make?", it: "Cosa vuoi realizzare?" })}</option>
                    {PROJECT_TYPES.map(([v, l]) => <option key={v} value={v}>{L(l)}</option>)}
                  </select>
                </Field>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label={L({ en: "Approximate quantity", it: "Quantità approssimativa" })} testid="field-quantity">
                    <select data-testid="contact-quantity-select" className={selectCls} value={form.quantity} onChange={set("quantity")}>
                      <option value="">{L({ en: "Select a range", it: "Seleziona un intervallo" })}</option>
                      {QUANTITIES.map(([v, l]) => <option key={v} value={v}>{L(l)}</option>)}
                    </select>
                  </Field>
                  <Field label={L({ en: "Preferred material", it: "Materiale preferito" })} testid="field-material">
                    <select data-testid="contact-material-select" className={selectCls} value={form.material} onChange={set("material")}>
                      <option value="">{L({ en: "Select if known", it: "Seleziona se già deciso" })}</option>
                      {MATERIALS.map(([v, l]) => <option key={v} value={v}>{L(l)}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label={L({ en: "Stone setting required?", it: "Incastonatura pietre?" })} testid="field-stones">
                  <select data-testid="contact-stones-select" className={selectCls} value={form.stones} onChange={set("stones")}>
                    {STONES.map(([v, l]) => <option key={v} value={v}>{L(l)}</option>)}
                  </select>
                </Field>
                <Field label={L({ en: "Tell us about your project", it: "Raccontaci il tuo progetto" })} testid="field-message">
                  <textarea
                    rows={5}
                    data-testid="contact-message-input"
                    className="forge-field resize-none"
                    placeholder={L({
                      en: "Describe your idea, design inspiration, techniques you have in mind, or questions you'd like answered.",
                      it: "Descrivi la tua idea, l'ispirazione, le tecniche che hai in mente o le domande a cui vorresti risposta.",
                    })}
                    value={form.message}
                    onChange={set("message")}
                  />
                  <span className="mt-1.5 block text-xs text-[#8A8F82]">
                    {L({ en: "Sketch, reference, written idea — anything works. You don't need all the answers.", it: "Bozzetto, riferimento, idea scritta — qualsiasi cosa. Non hai bisogno di tutte le risposte." })}
                  </span>
                </Field>
              </div>
            </div>

            <div className="mb-10 border-b border-[#0E3B33]/10 pb-10">
              <SectionTitle>{L({ en: "Reference files (optional)", it: "File di riferimento (opzionale)" })}</SectionTitle>
              <label
                data-testid="contact-upload-zone"
                className="block cursor-pointer border border-dashed border-[#0E3B33]/20 p-8 text-center transition-colors hover:border-[#C9A84C]"
              >
                <UploadCloud className="mx-auto mb-3 h-6 w-6 text-[#A8853B]" strokeWidth={1.5} />
                <p className="text-sm text-[#4A5248]">{L({ en: "Upload a sketch, reference image, or CAD file", it: "Carica un bozzetto, immagine di riferimento o file CAD" })}</p>
                <p className="mt-1 text-xs text-[#8A8F82]">{L({ en: "Click to browse or drag and drop", it: "Clicca per sfogliare o trascina" })}</p>
                <p className="mt-1 text-[11px] text-[#8A8F82]">JPG, PNG, PDF, DXF, STL — max 10MB</p>
                <input
                  type="file" multiple accept=".jpg,.jpeg,.png,.pdf,.dxf,.stl,.obj" className="hidden"
                  data-testid="contact-file-input"
                  onChange={uploadFiles}
                />
              </label>
              {uploading && (
                <p className="mt-2 text-xs text-[#8A8F82]" data-testid="contact-uploading">{L({ en: "Uploading…", it: "Caricamento…" })}</p>
              )}
              {fileNames.length > 0 && (
                <ul className="mt-2 space-y-1" data-testid="contact-file-names">
                  {fileNames.map((f) => (
                    <li key={f.id} className="flex items-center gap-2 text-xs text-[#A8853B]">
                      ✓ {f.name} <span className="text-[#8A8F82]">({Math.max(1, Math.round(f.size / 1024))} KB)</span>
                      <button
                        type="button"
                        data-testid={`contact-file-remove-${f.id}`}
                        onClick={() => setFileNames((prev) => prev.filter((x) => x.id !== f.id))}
                        className="text-[#8A8F82] transition-colors hover:text-red-700"
                      >
                        ×
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="mb-10">
              <SectionTitle>{L({ en: "Timeline", it: "Tempistiche" })}</SectionTitle>
              <Field label={L({ en: "When do you need the finished pieces?", it: "Quando hai bisogno dei pezzi finiti?" })} testid="field-timeline">
                <select data-testid="contact-timeline-select" className={selectCls} value={form.timeline} onChange={set("timeline")}>
                  <option value="">{L({ en: "Select if known", it: "Seleziona se già deciso" })}</option>
                  {TIMELINES.map(([v, l]) => <option key={v} value={v}>{L(l)}</option>)}
                </select>
                <span className="mt-1.5 block text-xs text-[#8A8F82]">
                  {L({ en: "We confirm realistic timelines based on your project scope.", it: "Confermiamo i tempi realistici in base all'ambito del progetto." })}
                </span>
              </Field>
            </div>

            <button type="submit" disabled={busy} data-testid="contact-submit-btn" className="ember-btn w-full justify-center disabled:opacity-60">
              {busy ? L({ en: "Sending…", it: "Invio…" }) : L({ en: "Send my brief →", it: "Invia il mio brief →" })}
            </button>
            <p className="mt-4 text-center text-[11px] leading-relaxed text-[#8A8F82]">
              {L({
                en: "We respond within 48 hours. Your brief and files are kept strictly confidential.",
                it: "Rispondiamo entro 48 ore. Il tuo brief e i tuoi file sono trattati con la massima riservatezza.",
              })}
            </p>
          </motion.form>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto max-w-md py-16 text-center"
            data-testid="contact-success"
          >
            <div className="mx-auto mb-8 flex h-16 w-16 items-center justify-center rounded-full border border-[#C9A84C]">
              <Check className="h-7 w-7 text-[#A8853B]" />
            </div>
            <div className="mx-auto mb-6 h-px w-14 bg-[#C9A84C]" />
            <h2 className="font-display text-3xl text-[#0E3B33]">{L({ en: "Brief received.", it: "Brief ricevuto." })}</h2>
            <p className="mt-4 text-sm leading-relaxed text-[#4A5248]">
              {L({
                en: "We'll review your project and come back to you within 48 hours. In the meantime, explore our services or follow our work on Instagram.",
                it: "Valuteremo il tuo progetto e ti risponderemo entro 48 ore. Nel frattempo, esplora i nostri servizi o seguici su Instagram.",
              })}
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a href="/" data-testid="success-home-link" className="ghost-btn !py-2.5 !px-5 text-sm">{L({ en: "Back to home", it: "Torna alla home" })}</a>
              <a href="https://instagram.com/dacreationsatelier" target="_blank" rel="noopener noreferrer" data-testid="success-instagram-link" className="ember-btn !py-2.5 !px-5 text-sm">@dacreationsatelier</a>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
}
